<template>
  <div class="pt-4 px-4 pb-20">
    <div class="mb-5 flex items-center justify-between">
      <div class="text-3xl font-bold">
        {{ $t('dashboard') }}
      </div>
      <button @click="onShowDashboardSettings" class="w-8 h-8 rounded-lg flex items-center justify-center border" :class="[ $theme.cardBorderColor]">
        <Cog6ToothIcon class="w-5 h-5 " :class="[$theme.iconFillSvg]"  />
      </button>
    </div>

    <div>
      <div class="block">
        <nav class="flex space-x-4 mb-5" aria-label="Tabs">
          <a
              v-for="tab in tabs"
              :key="tab.key"
              href="javascript:void(0)"
              :class="[
              tab.key === activePeriod
                ? `${$theme.activeTabBg} ${$theme.activeTabBorder} ${$theme.activeTabTextColor}`
                : `${$theme.tabTextColor} border-transparent opacity-70`,
              'rounded-lg px-3 py-2 border text-sm font-medium'
            ]"
              @click="onChangePeriod(tab.key)"
          >{{ tab.name }}</a>
        </nav>
      </div>

      <div class="rounded-xl overflow-hidden" :class="[$theme.cardBg]">
        <template v-if="loading || chartLoading">
          <ChartPlaceholder />
        </template>
        <template v-else>
          <TheChart
              :key="`line-${activePeriod}`"
              :chart_data="chartData"
              :deposit="dashboard.user_stats.balance ?? 0"
          />

          <dl class="mx-auto border-t grid gap-px grid-cols-2" :class="[$theme.cardBorderColor]">
            <div class="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2 px-3 py-3 border-r" :class="[$theme.cardBorderColor]">
              <dt class="text-sm font-medium" :class="[$theme.cardLabelTextColor]">P&L (USDT)</dt>
              <dd :class="[dashboard.percent_pnl < 0 ? $theme.dangerPnlColor : $theme.successPnlColor, 'text-xs font-medium']">
                {{ roundAmount(dashboard.percent_pnl) }}%
              </dd>
              <dd
                  class="w-full flex-none text-xl font-medium tracking-tight"
                  :class="[dashboard.pnl < 0 ? $theme.dangerPnlColor : $theme.successPnlColor]"
              >
                {{ roundAmount(dashboard.pnl)}}
              </dd>
            </div>
            <div class="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2 px-3 py-3">
              <dt class="text-sm font-medium" :class="[$theme.cardLabelTextColor]">
                {{ $t('close_positions') }}
              </dt>
              <dd class="w-full flex-none text-xl font-medium tracking-tight" :class="[$theme.baseTextColor]">
                {{ dashboard.closed_positions || 0 }}
              </dd>
            </div>
          </dl>
        </template>
      </div>

      <div v-if="dashboardSettings.referrals" class="mt-4">
        <div class="mb-1 flex items-center justify-between">
          {{ $t('referrals') }}
          <router-link to="/ref-stats" class="flex items-center text-sm" :class="$theme.textOrange">
            {{ $t('read_more') }}
            <ChevronRightIcon class="w-4 ml-1" />
          </router-link>
        </div>
        <div class="rounded-xl overflow-hidden" :class="[$theme.cardBg]">
          <template v-if="loading">
            <StatCardPlaceholder />
          </template>
          <template v-else>
            <dl class="mx-auto grid gap-px grid-cols-2">
              <div class="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2 px-3 py-3 border-r" :class="[$theme.cardBorderColor]">
                <dt class="text-sm font-medium" :class="[$theme.cardLabelTextColor]">
                  {{ $t('income') }}
                </dt>
                <dd
                    class="w-full flex-none text-xl font-medium tracking-tight"
                    :class="[dashboard.referral_income < 0 ? $theme.dangerPnlColor : $theme.successPnlColor]"
                >
                  ${{ dashboard.referral_income }}
                </dd>
              </div>
              <div class="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2 px-3 py-3">
                <dt class="text-sm font-medium" :class="[$theme.cardLabelTextColor]">{{ $t('referrals') }}</dt>
                <dd class="w-full flex-none text-xl font-medium tracking-tight" :class="[$theme.baseTextColor]">
                  {{ dashboard.referrals }}
                </dd>
              </div>
            </dl>
          </template>
        </div>
      </div>

      <div v-if="dashboardSettings.faq" class="mt-6 mb-3">
        <div class="mb-3 rounded-xl overflow-hidden" :class="[$theme.cardBg]">
          <template v-if="faqFeatures">
            <TheFaq :items="faqFeatures.data" />
          </template>
        </div>
        <template v-if="faqFeatures">
          <router-link v-if="faqFeatures" to="/faq" type="button" class="w-full flex items-center justify-center gap-2 rounded-xl px-3.5 py-2.5" :class="[
              $theme.lightButtonBg,
              $theme.lightButtonTextColor,
          ]">
            <BookIcon />
            {{ $t('find_more') }}
          </router-link>
        </template>
      </div>
    </div>
  </div>

  <TransitionRoot as="template" :show="openDashboardSettings">
    <Dialog class="relative z-50" @close="openDashboardSettings = false">
      <div class="fixed inset-0 bg-gray-950 bg-opacity-45 transition-opacity" />
      <div class="fixed inset-0 z-50 flex items-end justify-center">
        <TransitionChild
            as="template"
            enter="transform transition ease-in-out duration-300"
            enter-from="translate-y-full opacity-0"
            enter-to="translate-y-0 opacity-100"
            leave="transform transition ease-in-out duration-300"
            leave-from="translate-y-0 opacity-100"
            leave-to="translate-y-full opacity-0"
        >
          <DialogPanel class="w-full rounded-t-2xl px-4 pt-5 pb-4 text-left shadow-orange sm:p-6" :class="[$theme.baseBg, $theme.baseTextColor]">
            <div>
              <div class="py-4">
                <SwitchGroup as="div" class="flex items-center justify-between">
                  <SwitchLabel as="span" class="ml-3">
                    <span class=" ">{{ $t('show_ref') }}</span>
                  </SwitchLabel>
                  <Switch
                      v-model="dashboardSettings.referrals"
                      :class="[dashboardSettings.referrals ? $theme.switchBgActive : $theme.switchBgInactive, $theme.switchRingFocus, 'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2']"
                  >
                    <span aria-hidden="true" :class="[dashboardSettings.referrals ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none inline-block size-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out']" />
                  </Switch>
                </SwitchGroup>
              </div>

              <div class="py-4">
                <SwitchGroup as="div" class="flex items-center justify-between">
                  <SwitchLabel as="span" class="ml-3">
                    <span class=" ">{{ $t('show_faq') }}</span>
                  </SwitchLabel>
                  <Switch
                      v-model="dashboardSettings.faq"
                      :class="[dashboardSettings.faq ? $theme.switchBgActive : $theme.switchBgInactive, $theme.switchRingFocus, 'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2']"
                  >
                    <span aria-hidden="true" :class="[dashboardSettings.faq ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none inline-block size-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out']" />
                  </Switch>
                </SwitchGroup>
              </div>
            </div>
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import TheChart from "@/components/elem/TheChart.vue";
import TheFaq from "@/components/TheFaq.vue";
import BookIcon from "@/components/icons/BookIcon.vue";

import ChartPlaceholder from "@/components/placeholders/ChartPlaceholder.vue";
import StatCardPlaceholder from "@/components/placeholders/StatCardPlaceholder.vue";
import { ChevronRightIcon } from "@heroicons/vue/24/outline";
import { Cog6ToothIcon } from "@heroicons/vue/20/solid";
import {
  Switch,
  SwitchGroup,
  SwitchLabel,
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue";
import { roundAmount } from '@/helpers/numberHelper'

export default {
  components: {
    TheChart,
    TheFaq,
    ChevronRightIcon,
    Cog6ToothIcon,
    ChartPlaceholder,
    StatCardPlaceholder,
    Switch,
    SwitchGroup,
    SwitchLabel,
    Dialog,
    DialogPanel,
    TransitionChild,
    TransitionRoot,
    BookIcon,
  },

  watch: {
    // сохраняем настройки при любых изменениях
    dashboardSettings: {
      handler() {
        this.updateUserSettings();
      },
      deep: true,
    },

    // при смене стиля — обновляем данные графика
    'dashboardSettings.chartStyle'(next, prev) {
      if (next !== prev) {
        this.fetchChart();
      }
    },
  },

  data() {
    return {
      loading: true,
      chartLoading: true,
      error: null,

      openDashboardSettings: false,
      dashboardSettings: {
        chartStyle: 'line',
        referrals: true,
        faq: true,
      },

      chartStyleOptions: [
        {
          id: 'line',
          title: this.$t('line_chart')
        },
        {
          id: 'bar',
          title: this.$t('bar_chart')
        },
      ],

      faqFeatures: null,
      dashboard: null,
      chartData: null,

      activePeriod: '7days',
      tabs: [
        { name: this.$t('7days'), key: '7days' },
        { name: this.$t('14days'), key: '14days' },
        { name: this.$t('30days'), key: 'month' },
      ],
      roundAmount
    }
  },

  methods: {
    onShowDashboardSettings() {
      this.openDashboardSettings = true;
    },

    onChangePeriod(period) {
      if (this.activePeriod !== period) {
        this.activePeriod = period;
        this.fetchData();
      }
    },

    fetchData() {
      this.loading = true;
      this.$api.get('/dashboard', { params: { period: this.activePeriod } })
          .then(result => {
            this.dashboard = result.data;
          })
          .catch(e => {
            this.error = e;
            console.error(e);
          })
          .finally(() => {
            this.loading = false;
            this.fetchChart();
          });
    },

    fetchChart() {
      this.chartLoading = true;
      this.chartData = null;

      this.$api.get('/dashboard/chart', {
        params: {
          period: this.activePeriod,
          style: this.dashboardSettings.chartStyle,
        }
      })
          .then(result => {
            this.chartData = result.data;
          })
          .catch(e => {
            this.error = e;
            console.error(e);
          })
          .finally(() => {
            this.chartLoading = false;
          });
    },

    onLoadFaqFeatures() {
      this.$api.get('/faq/features')
          .then(result => {
            this.faqFeatures = result.data;
          })
          .catch(e => {
            this.error = e;
            console.error(e);
          });
    },

    updateUserSettings() {
      this.$api
          .post("/user/update-settings", {
            dashboard: this.dashboardSettings,
          })
          .then((result) => {
            // ok
            console.log("Settings updated.", result);
          })
          .catch((e) => {
            console.error(e);
            this.error = e;
          });
    },
  },

  mounted() {
    // подхватить сохранённые настройки пользователя, не теряя дефолты
    const saved = this.$user?.settings?.dashboard || {};
    this.dashboardSettings = { ...this.dashboardSettings, ...saved };

    this.fetchData();
    this.onLoadFaqFeatures();
  }
}
</script>
